import React, {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useSuggestedTags from 'Pods/SearchPage/Shared/useSuggestedTags'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { addTag, clearTags, removeTag } from 'Pods/SearchPage/Store/actions/tags'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

// 4 rows plus the focus ring padding; must match the `tags-content__tags--collapsed` max-height in
// _tags_filter.scss.
const COLLAPSED_MAX_HEIGHT = 116

// Body of the tag/category suggestion filter in the All Filters drawer (below 640). Wider
// layouts pick tags from the search-within panel's related searches.
function TagsContent({ hideReset = false, collapsible = false }) {
  const t = useTranslate()
  const trackFilterSelect = useTrackFilterSelect()

  const assetType = useSearchPageStore(state => state.assetType)
  const selectedTags = useSearchPageStore(state => state.tags)
  const suggestedTags = useSuggestedTags()
  const [searchTerm, setSearchTerm] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const tagsRef = useRef(null)
  // Initialized with current assetType so the effect skips on mount; only fires on real changes.
  // Only the local search term resets here - the tags themselves are cleared by setAssetType
  // (user-initiated changes only), so popstate restores keep their hydrated tags.
  const previousAssetTypeRef = useRef(assetType)

  useEffect(() => {
    if (previousAssetTypeRef.current === assetType) return
    previousAssetTypeRef.current = assetType
    setSearchTerm('')
  }, [assetType])

  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags])

  // Selected tags first (in selection order), then backend suggestions; deduped.
  // Set preserves insertion order, so this gives [...selected, ...unselectedSuggestions].
  const availableTags = useMemo(() => {
    const ordered = Array.from(new Set([...selectedTags, ...suggestedTags]))
    if (!searchTerm) return ordered
    const normalized = searchTerm.toLowerCase()
    return ordered.filter(tag => tag.toLowerCase().includes(normalized))
  }, [selectedTags, suggestedTags, searchTerm])

  // Surface the toggle only when the tags exceed 4 rows; remeasure on width and list changes.
  useEffect(() => {
    if (!collapsible) return undefined
    const el = tagsRef.current
    // Filtering to no matches unmounts the list; drop the stale toggle instead of keeping it
    // under the "No matches" text.
    if (!el) {
      setHasOverflow(false)
      return undefined
    }
    const measure = () => setHasOverflow(el.scrollHeight > COLLAPSED_MAX_HEIGHT + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [collapsible, availableTags])

  const hasSelectedAnyFilters = selectedTags.length > 0
  const isCollapsed = collapsible && !showAll

  const handleToggle = (tag) => {
    if (selectedSet.has(tag)) {
      removeTag(tag)
    } else {
      trackFilterSelect(FILTER_NAME.TAGS, tag)
      addTag(tag)
    }
  }

  const handleToggleShowAll = useCallback(() => setShowAll(value => !value), [])

  const tagsClassName = isCollapsed ? 'tags-content__tags tags-content__tags--collapsed' : 'tags-content__tags'

  return (
    <div className="tags-content">
      <FilterSearchInput
        id="tags-content-search"
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={t('search_page.filters.tags.find_placeholder')}
      />
      {availableTags.length > 0 ? (
        <div
          ref={tagsRef}
          className={tagsClassName}
          role="group"
          aria-label={t('search_page.filters.tags.tags_group_label')}
        >
          {availableTags.map(tag => (
            <Button
              key={tag}
              type="secondary"
              size="tiny"
              isActive={selectedSet.has(tag)}
              aria-pressed={selectedSet.has(tag)}
              onClick={() => handleToggle(tag)}
              customClass="tags-content__tag"
            >
              {tag}
            </Button>
          ))}
        </div>
      ) : (
        <span className="tags-content__empty cgt-body-base">
          {t('search_page.filters.tags.no_matches')}
        </span>
      )}
      {collapsible && hasOverflow && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={handleToggleShowAll}
        >
          {t(`search_page.filters.tags.${showAll ? 'show_less' : 'show_more'}`)}
        </Button>
      )}
      {!hideReset && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline"
          onClick={clearTags}
          disabled={!hasSelectedAnyFilters}
        >
          {t('search_page.filters.tags.reset')}
        </Button>
      )}
    </div>
  )
}

TagsContent.propTypes = {
  hideReset: bool,
  collapsible: bool,
}

export default memo(TagsContent)
